import React, { useCallback } from 'react';

import type { AddressType } from '@sg/garnish';
import {
  useSafeIsFocused,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { ConfirmLocation } from '../../../../components/ConfirmLocation';
import { useLastValidOrder } from '../../../../context/Order';
import { useCart } from '../../../../hooks';
import { useNavigateBack } from '../../../../navigation';
import { useLocationConfirm } from '../../hooks';
import { useOrderProductConfirmLocationScreenData } from './hooks/useOrderProductConfirmLocationScreenData';

export const OrderProductConfirmLocationScreen = () => {
  const { productSlug: baseProductSlug } = useSafeRouteParams<{
    productSlug: string;
  }>();

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const isFocused = useSafeIsFocused();
  const navigateBack = useNavigateBack();

  const { lastValidOrder: lastPlacedOrder } = useLastValidOrder();
  const { cart, deliveryDetails, cartOrderType, isCartEmpty } = useCart();

  const lastPlacedOrderDeliveryDetail = lastPlacedOrder?.deliveryOrderDetail;
  const hasCart = !isCartEmpty;
  const hasLocationHistory = hasCart || lastPlacedOrder;

  const { region, restaurant, deliveryAddress, pins, isFetching } =
    useOrderProductConfirmLocationScreenData({
      restaurantSlug: hasCart ? cart?.restaurant.id : lastPlacedOrder?.restaurant.id,
      addressId: hasCart
        ? deliveryDetails?.address?.id
        : lastPlacedOrder?.deliveryOrderDetail?.address?.id,
    });

  const deliveryVendorName = hasCart
    ? deliveryDetails.vendor
    : lastPlacedOrderDeliveryDetail?.vendor;
  const deliveryVendorRestaurantId = hasCart
    ? deliveryDetails.vendorRestaurantId
    : lastPlacedOrderDeliveryDetail?.vendorRestaurantId;
  const deliveryFee = hasCart
    ? deliveryDetails.deliveryFee
    : lastPlacedOrderDeliveryDetail?.deliveryFee;
  const orderType = (
    hasCart ? cartOrderType : lastPlacedOrder?.orderType
  )?.toLowerCase() as AddressType;

  // ─────────────────────────────────────────────────────────────────────

  // @ts-expect-error - isAddingLineItemToCart no longer returned by useLocationConfirm
  const { isFetchingProduct, isAddingLineItemToCart, handleLocationConfirm } = useLocationConfirm({
    baseProductSlug,
    deliveryFee,
    deliveryVendorName,
    deliveryVendorRestaurantId,
    restaurantId: restaurant?.id,
    restaurantSlug: restaurant?.slug,
    deliveryAddressId: deliveryAddress?.id,
  });

  // ─────────────────────────────────────────────────────────────────────

  const navigateToSearchLocation = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate({
        pathname: '/order-product/[productSlug]/search-location',
        params: { productSlug: baseProductSlug },
      });
      return;
    }

    navigation?.navigate('Modal', {
      screen: 'OrderProductSearchLocation',
      params: { productSlug: baseProductSlug },
    });
  }, [isExpoRouterEnabled, baseProductSlug, navigation, router]);

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isFocused) return null;

  return (
    <ConfirmLocation
      deliveryAddress={deliveryAddress}
      isFetching={isFetching}
      orderType={orderType === 'delivery' ? 'delivery' : 'pickup'}
      // @ts-expect-error TS(2322): Type '{ readonly __typename?: "Restaurant" | undef... Remove this comment to see the full error message
      restaurant={restaurant}
      region={region}
      pins={pins}
      isLoading={isFetchingProduct || isAddingLineItemToCart}
      onContinue={handleLocationConfirm}
      onClose={handleDismiss}
      onMoreLocations={hasCart ? undefined : navigateToSearchLocation}
      hasError={!region || !restaurant || !hasLocationHistory}
    />
  );
};
